// donut-array.ts
type Padding = { top: number; right: number; bottom: number; left: number }

export interface DonutDatum {
	label: string
	color: string // 메인 조각 색
	data: number[] // ← 여러 값 (호버 시 '뒤'로 펼침)
}

export interface DonutConfig {
	width?: number
	height?: number
	padding?: Padding
	title?: string
	data: DonutDatum[]
	innerRadius?: number // 도넛 안쪽 반지름
	ringWidth?: number // 도넛 두께
	gapDeg?: number // 조각 사이 간격(도)
	startAngle?: number // 기본 -90 (12시)
	maxStripeValue?: number // 뒤쪽 stripe의 기준 최대값 (없으면 데이터에서 자동)
	stripeWidth?: number // 뒤쪽 stripe 두께
	stripeGap?: number // 뒤쪽 stripe 간격
	sweepAngle?: number
	outerFanWidth?: number
}

export function renderDonutChart(mountTarget: string | HTMLElement, cfg: DonutConfig) {
	const mount = (typeof mountTarget === 'string' ? document.querySelector<HTMLElement>(mountTarget) : mountTarget)!
	if (!mount) throw new Error('mount element not found')

	const {
		width = 900,
		height = 520,
		padding = { top: 20, right: 20, bottom: 20, left: 20 },
		title = 'Donut chart name',
		data,
		innerRadius = 48,
		ringWidth = 56,
		gapDeg = 2,
		startAngle = -120,
		maxStripeValue,
		stripeWidth = 10,
		stripeGap = 4,
	} = cfg

	// 초기화 / 기본
	mount.innerHTML = ''
	mount.style.position = 'relative'
	mount.style.overflow = 'visible'
	const NS = 'http://www.w3.org/2000/svg'

	// 레이아웃: 오른쪽 라벨/툴팁 영역 확보
	const rightGutter = 260
	const cx = padding.left + Math.min(width - padding.left - padding.right - rightGutter, height - padding.top - padding.bottom) / 2 + ringWidth / 2
	const cy = padding.top + (height - padding.top - padding.bottom) / 2
	const R0 = innerRadius
	const R1 = innerRadius + ringWidth

	// 도넛 조각 각도 분배용 총합(값 배열 합으로 분배)
	const sliceTotals = data.map(d =>
		Math.max(
			0,
			d.data.reduce((a, v) => a + (+v || 0), 0),
		),
	)
	const total = Math.max(
		1,
		sliceTotals.reduce((a, b) => a + b, 0),
	)

	// 뒤쪽 stripe 최대값(비율 스케일)
	const autoMaxStripe = Math.max(1, ...data.flatMap(d => d.data.map(v => Math.max(0, +v || 0))))
	const stripeMax = maxStripeValue ?? autoMaxStripe

	// 유틸
	const deg2rad = (d: number) => (d * Math.PI) / 180
	const polar = (r: number, aDeg: number) => {
		const a = deg2rad(aDeg)
		return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as [number, number]
	}
	function donutPath(a0: number, a1: number): string {
		const [x0o, y0o] = polar(R1, a0)
		const [x1o, y1o] = polar(R1, a1)
		const [x0i, y0i] = polar(R0, a0)
		const [x1i, y1i] = polar(R0, a1)
		const large = Math.abs(a1 - a0) > 180 ? 1 : 0
		const sweep = a1 > a0 ? 1 : 0
		return `M ${x0o} ${y0o} A ${R1} ${R1} 0 ${large} ${sweep} ${x1o} ${y1o} L ${x1i} ${y1i} A ${R0} ${R0} 0 ${large} ${sweep ^ 1} ${x0i} ${y0i} Z`
	}

	function el<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, text?: string): SVGElementTagNameMap[K] {
		const e = document.createElementNS(NS, tag) as SVGElementTagNameMap[K]
		Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v))
		if (text != null) e.textContent = text
		return e
	}

	// SVG & 스타일
	const svg = el('svg', {
		width: String(width),
		height: String(height),
		viewBox: `0 0 ${width} ${height}`,
		style: 'background:#fff;border-radius:12px;',
	}) as SVGSVGElement

	const style = document.createElement('style')
	style.textContent = `
    .title { fill:#2b2b2b; font-weight:700; font-size:20px; font-family: ui-sans-serif,-apple-system,"Noto Sans KR",Arial; }
    .legend text { fill:#6b7280; font-size:12px; font-family: ui-sans-serif,-apple-system,"Noto Sans KR",Arial; }
    .slice { cursor:pointer; transform-box: fill-box; transform-origin: center; transition: transform .22s ease, fill-opacity .12s ease; }
    .colored { fill-opacity:0; }
    .sep { stroke:#fff; stroke-width:2; }
    .hole { fill:#111827; opacity:.85; filter:url(#holeShadow); }
    .stripe { fill:none; stroke-linecap:round; opacity:.95 }
    .tooltip {
      position:absolute; pointer-events:none; background:#fff; color:#111;
      padding:10px 12px; border-radius:12px; box-shadow:0 8px 32px rgba(0,0,0,.18);
      font-size:13px; font-family: ui-sans-serif,-apple-system,"Noto Sans KR",Arial;
      opacity:0; transition:opacity .12s ease; white-space:nowrap;
    }`
	mount.appendChild(style)

	// defs (중앙 작은 그림자)
	const defs = el('defs')
	const f = el('filter', { id: 'holeShadow', x: '-30%', y: '-30%', width: '160%', height: '160%' })
	f.appendChild(el('feGaussianBlur', { stdDeviation: '1.2', result: 'b' }))
	const fm = el('feMerge')
	fm.appendChild(el('feMergeNode', { in: 'b' }))
	fm.appendChild(el('feMergeNode', { in: 'SourceGraphic' }))
	f.appendChild(fm)
	defs.appendChild(f)
	svg.appendChild(defs)

	// 제목/범례
	svg.appendChild(el('text', { x: '16', y: '28', class: 'title' }, title))
	const legend = el('g', { class: 'legend' })
	let lx = 20,
		ly = 50
	data.slice(0, 4).forEach((d, i) => {
		legend.appendChild(el('rect', { x: String(lx), y: String(ly - 10), width: '14', height: '14', rx: '3', fill: d.color }))
		legend.appendChild(el('text', { x: String(lx + 20), y: String(ly + 2) }, d.label))
		lx += 75
	})
	svg.appendChild(legend)
	// 레이어: 뒤쪽 스트라이프(호버 때만 그려/갱신), 베이스/컬러 조각
	const stripesLayer = el('g') // ← 뒤쪽(도넛 바깥) 값 배열
	const slicesBase = el('g')
	const slicesColor = el('g') // 컬러 레이어(호버 때 보임)
	svg.appendChild(stripesLayer)
	svg.appendChild(slicesBase)
	svg.appendChild(slicesColor)

	// 각도 분배 & path 생성
	let ang = startAngle
	data.forEach((d, idx) => {
		const sweep = (sliceTotals[idx] / total) * (360 - gapDeg * data.length)
		const a0 = ang,
			a1 = ang + sweep
		ang = a1 + gapDeg

		// 회색 베이스(항상 보임)
		const base = el('path', { d: donutPath(a0, a1), class: 'slice base', fill: d.color })
		slicesBase.appendChild(base)

		// 이벤트 타깃: 컬러/베이스 모두에 붙여 hover 받기
		const enter = (e: MouseEvent) => show(e, d, a0, a1, base)
		const move = (e: MouseEvent) => moveTooltip(e)
		const leave = () => hide(base)

		;[base].forEach(t => {
			t.addEventListener('mouseenter', enter)
			t.addEventListener('mousemove', move)
			t.addEventListener('mouseleave', leave)
		})
	})

	// 툴팁
	const tip = document.createElement('div')
	tip.className = 'tooltip'
	tip.style.position = 'fixed'
	mount.appendChild(tip)

	// 세그먼트 path 헬퍼 (파일 상단에 추가되어 있지 않다면 추가)
	function ringSegmentPath(rIn: number, rOut: number, a0: number, a1: number): string {
		const [x0o, y0o] = polar(rOut, a0)
		const [x1o, y1o] = polar(rOut, a1)
		const [x0i, y0i] = polar(rIn, a0)
		const [x1i, y1i] = polar(rIn, a1)
		const large = Math.abs(a1 - a0) > 180 ? 1 : 0
		const sweep = a1 > a0 ? 1 : 0
		return [`M ${x0o} ${y0o}`, `A ${rOut} ${rOut} 0 ${large} ${sweep} ${x1o} ${y1o}`, `L ${x1i} ${y1i}`, `A ${rIn} ${rIn} 0 ${large} ${sweep ^ 1} ${x0i} ${y0i}`, 'Z'].join(' ')
	}

	// 방사형(중심→바깥) 선 하나
	function radialLinePath(r0: number, r1: number, a: number): string {
		const [x0, y0] = polar(r0, a)
		const [x1, y1] = polar(r1, a)
		return `M ${x0} ${y0} L ${x1} ${y1}`
	}
	let shadowPath: any = null
	function show(e: any, d: DonutDatum, a0: number, a1: number, colored: any) {
		// 1) 메인 조각 강조
		colored.style.fillOpacity = '1'
		const mid = (a0 + a1) / 2
		const offset = 8
		colored.style.transform = `translate(${Math.cos(deg2rad(mid)) * offset}px, ${Math.sin(deg2rad(mid)) * offset}px)`
		shadowPath = colored.cloneNode(true)
		shadowPath?.setAttribute('fill', d.color)
		shadowPath?.setAttribute('opacity', '0.9')
		shadowPath?.setAttribute('transform', `translate(4, 4)`)
		shadowPath?.setAttribute('filter', 'blur(10px)')
		colored?.parentNode.insertBefore(shadowPath, colored)

		// 2) 바깥 "부채" 하나 그리고, 값 배열로 방사형 경계선 긋기
		stripesLayer.innerHTML = '' // reset
		const fanGap = 10 // 내부 도넛과의 간격
		const fanIn = R1 + fanGap // 부채 안쪽 반지름
		const fanW = cfg.outerFanWidth ?? 90 // 부채 두께(조절 포인트)
		const fanOut = fanIn + fanW // 부채 바깥 반지름

		// (a) 부채 채우기: 한 장으로 색 채움
		const fan = el('path', {
			d: ringSegmentPath(fanIn + 5, fanOut, a0 + 2, a1 - 2),
			fill: d.color,
			opacity: '.6',
		})
		stripesLayer.appendChild(fan)

		// (b) 값 배열 누적비율로 방사형 경계선 (values 길이가 N이면 N-1개)
		const sum = Math.max(
			1,
			d.data.reduce((acc, v) => acc + Math.max(0, +v || 0), 0),
		)
		let acc = 0
		d.data.slice(0, -1).forEach(v => {
			acc += Math.max(0, +v || 0)
			const t = acc / sum // 누적 비율
			const a = a0 + (a1 - a0) * t // 경계선 각도
			const line = el('path', {
				d: radialLinePath(fanIn, fanOut, a),
				stroke: '#FFF', // 이미지처럼 진한 선
				'stroke-width': '3',
				fill: 'none',
			})
			stripesLayer.appendChild(line)
		})

		// (c) 도넛과 부채의 경계에 살짝 dotted 느낌(옵션)
		const innerBorder = el('path', {
			d: radialLinePath(fanIn, fanIn, a0), // dummy path (우회)
		})
		// 실제 dotted는 같은 반경의 arc가 필요:
		innerBorder.setAttribute('d', `M ${polar(fanIn, a0).join(' ')} A ${fanIn} ${fanIn} 0 ${Math.abs(a1 - a0) > 180 ? 1 : 0} ${a1 > a0 ? 1 : 0} ${polar(fanIn, a1).join(' ')}`)
		innerBorder.setAttribute('stroke', '#ffffff')
		innerBorder.setAttribute('stroke-width', '3')
		innerBorder.setAttribute('stroke-dasharray', '2 6')
		innerBorder.setAttribute('fill', 'none')
		innerBorder.setAttribute('opacity', '.9')
		stripesLayer.appendChild(innerBorder)

		// 3) 툴팁 (오른쪽 고정)
		tip.innerHTML = `
	<span style="display:inline-block;width:10px;height:10px;border-radius:3px;background:${d.color};"></span>
    <span style="font-weight:700;margin-bottom:6px">${d.label}</span>
    ${d.data
		.map(
			(v, i) => `
      <div style="display:flex;align-items:center;gap:8px;margin:2px 0;">
        <span>값 ${i + 1} : <b>${v}</b></span>
      </div>
    `,
		)
		.join('')}
  `
		tip.style.left = `${e.clientX}px`
		tip.style.top = `${e.clientY}px`
		tip.style.opacity = '1'
	}

	function hide(colored: SVGPathElement) {
		colored.style.transform = 'translate(0,0)'
		tip.style.opacity = '0'
		stripesLayer.innerHTML = ''
		shadowPath.remove()
	}

	function moveTooltip(e: MouseEvent) {
		tip.style.left = `${e.clientX + 20}px`
		tip.style.top = `${e.clientY}px`
	}
	mount.appendChild(svg)
}
